import type { HourPoint, WeatherReport } from '../../shared/contract'
import { localDate } from '../../shared/units'

const HOUR = 3_600_000
const finite = (value: number | null | undefined): value is number => value != null && Number.isFinite(value)

/** Keep the sample itself, so every surface names the same instant when minima tie. */
export function hourlyExtrema(hours: readonly HourPoint[]) {
  let low: HourPoint | null = null, high: HourPoint | null = null
  for (const hour of hours) {
    if (!finite(hour.temperature_c)) continue
    if (!low || hour.temperature_c < low.temperature_c!) low = hour
    if (!high || hour.temperature_c > high.temperature_c!) high = hour
  }
  return { low, high }
}

const prepared = new WeakMap<WeatherReport, Map<string, { low: number | null; high: number | null }>>()
/** A supplied calendar-day range must contain its hourly samples. Missing daily bounds stay unknown. */
export function dailyTemperatures(report: WeatherReport) {
  const hit = prepared.get(report)
  if (hit) return hit
  const days = new Map(report.daily.map(day => [day.date, {
    low: finite(day.low_c) ? day.low_c : null, high: finite(day.high_c) ? day.high_c : null,
  }]))
  for (const hour of report.hourly) {
    if (!finite(hour.temperature_c) || !Number.isFinite(Date.parse(hour.time))) continue
    const day = days.get(localDate(hour.time, report.location.timezone))
    if (day?.low != null) day.low = Math.min(day.low, hour.temperature_c)
    if (day?.high != null) day.high = Math.max(day.high, hour.temperature_c)
  }
  prepared.set(report, days)
  return days
}

/** The next contiguous night through its first daylight sample, including the dawn minimum. */
export function comingNight(report: WeatherReport) {
  const now = Date.parse(report.current.time)
  const hours = report.hourly.filter(hour => Date.parse(hour.time) >= Math.floor(now / HOUR) * HOUR && Date.parse(hour.time) <= now + 36 * HOUR).sort((a, b) => Date.parse(a.time) - Date.parse(b.time))
  const start = hours.findIndex(hour => !hour.is_day)
  if (start < 0) return null
  let end = start
  while (end + 1 < hours.length && !hours[end].is_day && Date.parse(hours[end + 1].time) - Date.parse(hours[end].time) <= HOUR * 1.5) end++
  const night = hours.slice(start, end + 1)
  if (!night.every(hour => finite(hour.temperature_c))) return null
  const lowest = hourlyExtrema(night).low!
  return { low: lowest.temperature_c!, at: Date.parse(lowest.time), start: Date.parse(night[0].time), end: Date.parse(night[night.length - 1].time), complete: hours[end].is_day && (start > 0 || !report.current.is_day) }
}
